if (!customElements.get("nw-buy-box")) {
  class NwBuyBox extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.formId = this.dataset.formId;
      this.sectionId = this.dataset.sectionId;
      this.data = JSON.parse(this.querySelector("[data-nw-buybox-json]").textContent);
      this.normalizeData();
      this.selectedFlavorIndex = Number(this.dataset.selectedFlavorIndex) || 0;
      this.selectedSize = this.dataset.selectedSize || "30";
      this.purchaseType = this.dataset.purchaseType || "subscription";
      this.syncing = false;

      this.flavorLabelEl = this.querySelector("[data-nw-flavor-value]");
      this.benefits30 = this.querySelector("[data-nw-benefits='30']");
      this.benefits90 = this.querySelector("[data-nw-benefits='90']");
      this.subTitle = this.querySelector("[data-nw-sub-title]");
      this.subSubtitle = this.querySelector("[data-nw-sub-subtitle]");
      this.subCompare = this.querySelector("[data-nw-sub-compare]");
      this.subSale = this.querySelector("[data-nw-sub-sale]");
      this.onetimeCompare = this.querySelector("[data-nw-onetime-compare]");
      this.onetimeSale = this.querySelector("[data-nw-onetime-sale]");

      this.querySelectorAll("[data-nw-flavor-index]").forEach((btn) => {
        btn.addEventListener("click", () => this.selectFlavor(Number(btn.dataset.nwFlavorIndex)));
      });
      this.querySelectorAll("[data-nw-size]").forEach((btn) => {
        btn.addEventListener("click", () => this.selectSize(btn.dataset.nwSize));
      });
      this.querySelectorAll("[data-nw-purchase]").forEach((btn) => {
        btn.addEventListener("click", () => this.selectPurchase(btn.dataset.nwPurchase));
      });

      this.render();
      this.syncSellingPlan();
      this.bindWhenFormReady();
    }

    normalizeData() {
      if (this.data.flavors) return;
      if (!this.data.variants) return;
      this.data.flavors = this.data.variants.map((item, index) => ({
        index,
        title: item.title,
        options: item.options,
        id30: item.id,
        id90: item.id,
        available30: item.available,
        available90: item.available,
        onetime30: item.price,
        compare30: item.compare_at,
        onetime90: item.price,
        compare90: item.compare_at,
        perServing30Sub: item.plans?.[this.data.plan30]?.per_serving || "",
        perServing30Otp: item.plans?.[this.data.plan30]?.per_serving || "",
        perServing90Sub: item.plans?.[this.data.plan90]?.per_serving || "",
        perServing90Otp: item.plans?.[this.data.plan90]?.per_serving || "",
        subscription30: {
          price: item.plans?.[this.data.plan30]?.price || item.price,
          compare: item.plans?.[this.data.plan30]?.compare || item.compare_at,
        },
        subscription90: {
          price: item.plans?.[this.data.plan90]?.price || item.price,
          compare: item.plans?.[this.data.plan90]?.compare || item.compare_at,
        },
      }));
      this.data.hasProduct90 = false;
    }

    get flavorEntry() {
      const flavors = this.data.flavors || [];
      return flavors[this.selectedFlavorIndex] || flavors[0];
    }

    get variant() {
      const flavor = this.flavorEntry;
      if (!flavor) return null;
      const is90 = this.selectedSize === "90";
      const plan =
        (is90 ? flavor.subscription90 : flavor.subscription30) ||
        (is90 ? flavor.plans90?.[this.data.plan90] : flavor.plans30?.[this.data.plan30]) ||
        {};
      return {
        id: is90 ? flavor.id90 : flavor.id30,
        title: flavor.title,
        available: is90 ? flavor.available90 : flavor.available30,
        options: flavor.options,
        price: is90 ? flavor.onetime90 : flavor.onetime30,
        compare_at: is90 ? flavor.compare90 : flavor.compare30,
        plan,
      };
    }

    get selectedVariantId() {
      return this.variant?.id;
    }

    get planId() {
      const id = this.selectedSize === "90" ? this.data.plan90 : this.data.plan30;
      if (id == null || id === "") return "";
      return String(id);
    }

    get unlockCount() {
      return this.selectedSize === "90" ? this.data.unlock90 : this.data.unlock30;
    }

    get mainQuantity() {
      const qty = this.selectedSize === "90" ? this.data.qty90 : this.data.qty30;
      const parsed = Number(qty);
      return parsed > 0 ? parsed : 1;
    }

    perServingForSize(size) {
      const flavor = this.flavorEntry;
      if (!flavor) return "";
      const sub = size === "90";
      if (this.purchaseType === "subscription") {
        return sub ? flavor.perServing90Sub : flavor.perServing30Sub;
      }
      return sub ? flavor.perServing90Otp : flavor.perServing30Otp;
    }

    selectFlavor(index) {
      this.selectedFlavorIndex = index;
      this.render();
      this.syncVariant();
      this.syncSellingPlan();
    }

    selectSize(size) {
      this.selectedSize = size;
      this.render();
      this.syncSellingPlan();
      this.syncQuantity();
      this.syncVariant();
    }

    selectPurchase(type) {
      this.purchaseType = type;
      this.render();
      this.syncSellingPlan();
    }

    render() {
      const variant = this.variant;
      const flavor = this.flavorEntry;
      if (!variant || !flavor) return;

      this.querySelectorAll("[data-nw-flavor-index]").forEach((btn) => {
        const index = Number(btn.dataset.nwFlavorIndex);
        const selected = index === this.selectedFlavorIndex;
        btn.classList.toggle("is-selected", selected);
        btn.setAttribute("aria-pressed", selected ? "true" : "false");
        const entry = this.data.flavors[index];
        const entryAvailable = entry
          ? this.selectedSize === "90"
            ? entry.available90
            : entry.available30
          : false;
        btn.disabled = !entryAvailable;
      });

      if (this.flavorLabelEl) this.flavorLabelEl.textContent = flavor.title;

      this.querySelectorAll("[data-nw-size]").forEach((btn) => {
        const selected = btn.dataset.nwSize === this.selectedSize;
        btn.classList.toggle("is-selected", selected);
        btn.setAttribute("aria-pressed", selected ? "true" : "false");
      });

      this.querySelectorAll("[data-nw-per-serving]").forEach((el) => {
        el.textContent = this.perServingForSize(el.dataset.nwPerServing) || "";
      });

      this.querySelectorAll("[data-nw-purchase]").forEach((btn) => {
        const selected = btn.dataset.nwPurchase === this.purchaseType;
        btn.classList.toggle("is-selected", selected);
        btn.setAttribute("aria-pressed", selected ? "true" : "false");
      });

      const subCopy = this.selectedSize === "90" ? this.data.copy90 : this.data.copy30;
      if (this.subTitle) this.subTitle.textContent = subCopy.title;
      if (this.subSubtitle) this.subSubtitle.textContent = subCopy.subtitle;
      if (this.benefits30) this.benefits30.hidden = this.selectedSize !== "30";
      if (this.benefits90) this.benefits90.hidden = this.selectedSize !== "90";

      const plan = variant.plan || {};
      if (this.subCompare) {
        const compare = plan.compare || variant.compare_at || "";
        const sale = plan.price || variant.price;
        this.subCompare.textContent = compare;
        this.subCompare.hidden = !compare || compare === sale;
      }
      if (this.subSale) this.subSale.textContent = plan.price || variant.price;
      if (this.onetimeCompare) {
        this.onetimeCompare.textContent = variant.compare_at || "";
        this.onetimeCompare.hidden = !variant.compare_at || variant.compare_at === variant.price;
      }
      if (this.onetimeSale) this.onetimeSale.textContent = variant.price;

      this.querySelectorAll("[data-nw-gift]").forEach((gift) => {
        const index = Number(gift.dataset.nwGift);
        const always = gift.dataset.nwAlways === "true";
        let unlocked = always || index <= this.unlockCount;
        if (this.purchaseType === "onetime") {
          unlocked = this.dataset.onetimeGifts === "true" && (index === 1 || always);
        }
        gift.classList.toggle("is-locked", !unlocked);

        const lockLabel = gift.querySelector(".nw-buybox__gift-lock-label");
        if (lockLabel) {
          const defaultLabel = lockLabel.dataset.nwLockDefault || lockLabel.textContent;
          lockLabel.textContent = this.purchaseType === "onetime" && subCopy.title ? subCopy.title : defaultLabel;
        }
      });
    }

    form() {
      return document.getElementById(this.formId);
    }

    sellingPlanInput() {
      const form = this.form();
      if (!form) return null;
      let input = form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "selling_plan";
        form.appendChild(input);
      }
      return input;
    }

    bindWhenFormReady() {
      const bind = () => {
        const form = this.form();
        if (!form) return false;
        this.bindExternalVariantSync();
        this.bindGiftCartSubmit(form);
        this.syncQuantity();
        this.syncVariant();
        return true;
      };
      if (bind()) return;
      document.addEventListener("DOMContentLoaded", bind, { once: true });
      const observer = new MutationObserver(() => {
        if (bind()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    bindExternalVariantSync() {
      const form = this.form();
      if (!form || form.dataset.nwVariantBound === "true") return;
      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      form.dataset.nwVariantBound = "true";
      idInput.addEventListener("change", () => {
        if (this.syncing) return;
        const nextId = Number(idInput.value);
        if (!nextId) return;
        const index = this.findFlavorIndexForExternalSync(nextId);
        if (index < 0) return;
        const flavorChanged = index !== this.selectedFlavorIndex;
        if (flavorChanged) this.selectedFlavorIndex = index;
        this.render();
        this.syncSellingPlan();
        this.syncVariant();
      });
    }

    findFlavorIndexByVariantId(variantId) {
      const flavors = this.data.flavors || [];
      return flavors.findIndex((item) => item.id30 === variantId || item.id90 === variantId);
    }

    findFlavorIndexForExternalSync(variantId) {
      const flavors = this.data.flavors || [];
      if (this.selectedSize === "90" && this.data.hasProduct90) {
        return flavors.findIndex((item) => item.id30 === variantId);
      }
      return this.findFlavorIndexByVariantId(variantId);
    }

    bindGiftCartSubmit(form) {
      if (form.dataset.nwGiftSubmitBound === "true") return;
      form.dataset.nwGiftSubmitBound = "true";
      form.addEventListener("submit", () => {
        this.applyDiscountCode();
        this.installFormDataPatch();
      }, true);

      const productForm = form.closest("product-form");
      if (productForm && typeof productForm.handleSubmit === "function" && productForm.dataset.nwGiftSubmitBound !== "true") {
        productForm.dataset.nwGiftSubmitBound = "true";
        const original = productForm.handleSubmit.bind(productForm);
        productForm.handleSubmit = (event) => {
          this.applyDiscountCode();
          this.installFormDataPatch();
          return original(event);
        };
      }
    }

    applyDiscountCode() {
      const code = (this.dataset.discountCode || "").trim();
      if (!code) return;
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/";
      fetch(`${root}discount/${encodeURIComponent(code)}`).catch(() => {});
    }

    getUnlockedGifts() {
      const gifts = [];
      const seen = new Set();
      const addGift = (gift, includePlan) => {
        const variantId = gift.dataset.nwGiftVariant;
        if (!variantId || seen.has(variantId)) return;
        seen.add(variantId);
        const sellingPlan = includePlan && gift.dataset.nwGiftPlan ? String(gift.dataset.nwGiftPlan) : "";
        gifts.push({
          id: String(variantId),
          sellingPlan,
        });
      };

      if (this.purchaseType === "onetime") {
        if (this.dataset.onetimeGifts !== "true") return [];
        this.querySelectorAll("[data-nw-gift]").forEach((gift) => {
          const index = Number(gift.dataset.nwGift);
          const always = gift.dataset.nwAlways === "true";
          if (index === 1 || always) addGift(gift, this.dataset.onetimeGiftPlans === "true");
        });
        return gifts;
      }

      if (this.purchaseType !== "subscription") return [];
      this.querySelectorAll("[data-nw-gift]").forEach((gift) => {
        if (gift.classList.contains("is-locked")) return;
        addGift(gift, true);
      });
      return gifts;
    }

    installFormDataPatch() {
      const gifts = this.getUnlockedGifts();
      if (!gifts.length || window.FormData.__nwBuyBoxPatched) return;

      const OriginalFormData = window.FormData;
      const form = this.form();
      const buyBox = this;

      class NwBuyBoxFormData extends OriginalFormData {
        constructor(...args) {
          super(...args);
          const formEl = args[0];
          if (formEl === form) buyBox.applyGiftsToFormData(this, formEl, gifts);
        }
      }

      NwBuyBoxFormData.__nwBuyBoxPatched = true;
      window.FormData = NwBuyBoxFormData;
      setTimeout(() => {
        if (window.FormData === NwBuyBoxFormData) window.FormData = OriginalFormData;
      }, 0);
    }

    applyGiftsToFormData(formData, form, gifts) {
      const mainId = String(
        this.selectedVariantId || form.querySelector('input[name="id"]')?.value || formData.get("id") || ""
      );
      if (!mainId) return;

      let index = 0;
      while (formData.has(`items[${index}][id]`)) index += 1;

      if (index === 0) {
        const quantity = String(this.mainQuantity);
        const sellingPlan = formData.get("selling_plan") || "";
        formData.append("items[0][id]", mainId);
        formData.append("items[0][quantity]", quantity);
        if (sellingPlan) formData.append("items[0][selling_plan]", sellingPlan);
        index = 1;
      }

      formData.delete("id");
      formData.delete("quantity");
      formData.delete("selling_plan");

      gifts.forEach((gift) => {
        if (!gift.id || gift.id === mainId) return;
        formData.append(`items[${index}][id]`, gift.id);
        formData.append(`items[${index}][quantity]`, "1");
        formData.append(`items[${index}][parent_id]`, mainId);
        formData.append(`items[${index}][properties][_free_gift]`, "true");
        if (gift.sellingPlan) {
          formData.append(`items[${index}][selling_plan]`, gift.sellingPlan);
        }
        index += 1;
      });
    }

    syncSellingPlan() {
      const planInput = this.sellingPlanInput();
      if (!planInput) return;
      planInput.value = this.purchaseType === "subscription" ? this.planId : "";
    }

    syncQuantity() {
      const qty = String(this.mainQuantity);
      const form = this.form();
      const inputs = new Set();
      if (form) {
        form.querySelectorAll('input[name="quantity"]').forEach((input) => inputs.add(input));
      }
      document.querySelectorAll(`#Quantity-${this.sectionId}`).forEach((input) => inputs.add(input));
      inputs.forEach((input) => {
        if (String(input.value) === qty) return;
        input.value = qty;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    syncVariant() {
      const variant = this.variant;
      if (!variant || this.syncing) return;
      this.syncing = true;

      try {
        const shouldSyncPicker = this.selectedSize === "30" || !this.data.hasProduct90;
        const pickerUpdated = shouldSyncPicker ? this.updateVariantPicker(variant) : false;
        if (shouldSyncPicker) this.updateStickyPicker(variant);

        const form = this.form();
        if (form) {
          const idInput = form.querySelector('input[name="id"]');
          if (idInput && String(idInput.value) !== String(variant.id)) {
            idInput.value = variant.id;
            idInput.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }

        if (shouldSyncPicker && !pickerUpdated && variant.options) {
          if (this.selectedSize === "30") {
            const url = new URL(window.location.href);
            url.searchParams.set("variant", String(variant.id));
            window.history.replaceState({}, "", url.toString());
          }
        }
      } finally {
        this.syncing = false;
      }
    }

    updateVariantPicker(variant) {
      const picker = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (!picker || !variant.options) return false;

      let changed = false;
      const groups = picker.querySelectorAll(".product-form__input");
      variant.options.forEach((value, index) => {
        const group = groups[index];
        if (!group) return;
        const select = group.querySelector("select");
        if (select) {
          if (select.value !== value) {
            select.value = value;
            changed = true;
          }
          return;
        }
        const radio = Array.from(group.querySelectorAll("input")).find((el) => el.value === value);
        if (radio && !radio.checked) {
          radio.checked = true;
          changed = true;
        }
      });

      if (changed) picker.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    updateStickyPicker(variant) {
      document.querySelectorAll(`#StickyAtcVariantPicker-${this.sectionId} select.variant-dropdown`).forEach((select) => {
        const optionValue = (variant.options || []).join(",");
        const option = Array.from(select.options).find((item) => item.dataset.options === optionValue);
        if (option) select.value = option.value;
      });
    }
  }

  customElements.define("nw-buy-box", NwBuyBox);
}
